/**
 * World — the headless battle simulation (TECHNICAL_ARCHITECTURE §3.2).
 *
 * Owns all entities + battle state and advances them on a fixed timestep via
 * `step(dt)`. Phaser-free and deterministic (seeded RNG), so it is unit-testable
 * headlessly and is the same engine that will later power auto-run/sweep and
 * solo-viability simulation. BattleScene constructs a World, calls `step` each
 * fixed tick, and renders from its state; HUD reads its state + calls the player
 * actions (deploy/upgrade/sell/targeting/call-wave).
 *
 * The systems (wave/spawn, status, path+blocking+leak, targeting+combat) live
 * here as clearly-sectioned private methods rather than separate files — the
 * slice favours one cohesive, well-tested orchestrator; they can be split out
 * later without changing this public surface.
 */
import { Path } from '@/battle/Path';
import { Enemy } from '@/battle/entities/Enemy';
import { Unit } from '@/battle/entities/Unit';
import { Soldier } from '@/battle/entities/Soldier';
import {
  DEFAULT_STATS,
  mergeStats,
  resolveTierStats,
  resolveBlockCount,
  resolveSquadSize,
  resolveDamage,
  type Stats,
} from '@/battle/Stats';
import {
  effectiveTiers,
  maxTierFor,
  isBranchPoint,
  tierDefAt,
} from '@/battle/Upgrades';
import { Rng } from '@/utils/rng';
import type { Unit as UnitDef } from '@/data/schema/unit';
import type { Enemy as EnemyDef } from '@/data/schema/enemy';
import type { Level, Slot, Wave, Difficulty } from '@/data/schema/level';
import type { TargetMode } from '@/data/schema/common';

export interface GameData {
  level: Level;
  units: Map<string, UnitDef>;
  enemies: Map<string, EnemyDef>;
  /** Which difficulty tier to play (defaults to the lowest defined / 1). */
  difficultyTier?: number;
}

export type Phase = 'prep' | 'spawning' | 'intermission' | 'won' | 'lost';

/** Ephemeral per-step events the renderer drains for tracers/popups/feedback. */
export type SimEvent =
  | { kind: 'shot'; fromX: number; fromY: number; toX: number; toY: number; family: string; toFlying: boolean }
  | { kind: 'damage'; x: number; y: number; amount: number; crit: boolean }
  | { kind: 'death'; x: number; y: number; bounty: number; defId: string }
  | { kind: 'unitDeath'; x: number; y: number }
  | { kind: 'unitRespawn'; x: number; y: number }
  | { kind: 'leak'; x: number; y: number; cost: number; defId: string };

export type ActionResult =
  | { ok: true }
  | { ok: false; reason: string };

const INTERMISSION_S = 12;

/**
 * Melee survivability (GDD pillar #1: blockers HOLD) is a finite, replenishing
 * pool — NOT a tank-heal. The rules:
 *  - While HOLDING a foe (in combat): NO regen. A soldier under attack only
 *    loses HP, so it can be worn down — a lone hero cannot solo a boss.
 *  - Idle (out of combat, mid-wave): knits wounds at `MELEE_REGEN_FRACTION_PER_S`
 *    of max HP per second, recovering between engagements.
 *  - Between waves: the squad is restored to FULL (and the fallen revive) — so
 *    a wave's beating doesn't carry over and each wave starts fresh.
 *  - On falling: respawns at full HP after `MELEE_RESPAWN_S` (it leaves the line
 *    briefly, so enemies can push past — the cost of letting it die).
 */
const MELEE_REGEN_FRACTION_PER_S = 0.05;
const MELEE_RESPAWN_S = 6;

/**
 * "Expose" — a MODEST damage bonus on an enemy held by a blocker that has the
 * `expose` ability. It is NOT inherent to all blocking: it's an earned upgrade
 * (footmen gain it on their shield-bash tier; the Warden has it). Archers still
 * do the bulk of the work on small/fast enemies — Expose is what makes holding
 * a tanky enemy (or, later, a boss) at a chokepoint worthwhile so the army can
 * burst it down. Forward path (M4): split melee upgrades into a tankier "hold"
 * line and a debuff-heavy "sunder" line; this perk is the seed of the latter.
 */
const EXPOSED_DAMAGE_MULT = 1.4;

/** Chain lightning: how far a bolt can jump, and the damage falloff per jump. */
const CHAIN_RANGE = 150;
const CHAIN_FALLOFF = 0.72;

interface GroupCursor {
  spawned: number;
  timer: number;
}

/**
 * How close (px) a melee slot must sit to a walk lane for its squad to physically
 * hold that lane. On a single-path stage every blocker covers the one lane; on a
 * branching stage this is what makes a blocker beside the LEFT cascade hold only
 * the left stream — while a slot on the shared run (where the lanes coincide)
 * holds both. Kept generous so authored lane-side slots reliably engage.
 */
const BLOCK_LANE_REACH = 130;

export class World {
  readonly data: GameData;
  /** Walk lanes. `path` (below) aliases lane 0 for all single-path consumers. */
  readonly paths: Path[];
  readonly path: Path;
  private readonly rng: Rng;
  private readonly difficulty: Difficulty;

  gold: number;
  lives: number;
  readonly startingLives: number;
  leaked = 0;
  /** Set when a boss reaches the gate — drives a distinct defeat banner. */
  bossBreached = false;

  phase: Phase = 'prep';
  waveIndex = -1;
  intermissionRemaining = 0;
  stars = 0;

  readonly enemies: Enemy[] = [];
  private readonly enemyPool: Enemy[] = [];
  /** Deployed units keyed by slot id. */
  readonly units = new Map<string, Unit>();

  /** Drained by the renderer each frame. */
  events: SimEvent[] = [];

  private cursors: GroupCursor[] = [];

  constructor(data: GameData, seed = 1) {
    this.data = data;
    this.paths = data.level.paths.map((pts) => new Path(pts));
    this.path = this.paths[0];
    this.rng = new Rng(seed);
    this.startingLives = data.level.startingLives;
    this.lives = data.level.startingLives;
    this.gold = data.level.startingGold;
    this.difficulty = this.resolveDifficulty();
  }

  /** The active difficulty tier number (e.g. 1–4). */
  get tier(): number {
    return this.difficulty.tier;
  }
  /** The active difficulty's display name (e.g. "Mortal"). */
  get difficultyName(): string {
    return this.difficulty.name;
  }

  /** The walk lane an enemy is travelling (its own path on a branching stage). */
  pathFor(e: Enemy): Path {
    return this.paths[e.lane] ?? this.paths[0];
  }

  private resolveDifficulty(): Difficulty {
    const ds = this.data.level.difficulties;
    const want = this.data.difficultyTier;
    const found =
      (want != null ? ds.find((d) => d.tier === want) : undefined) ?? ds[0];
    return (
      found ?? {
        tier: 1,
        name: 'Mortal',
        enemyHp: 1,
        enemyDmg: 1,
        rewardMult: 1,
        postGame: false,
      }
    );
  }

  // ====================================================================== //
  //  Fixed-step update                                                     //
  // ====================================================================== //

  step(dt: number): void {
    if (this.phase === 'won' || this.phase === 'lost') return;

    this.updateWaves(dt);
    this.updateStatuses(dt);
    this.updateUnits(dt);
    this.updatePathAndBlocking(dt);
    this.updateCombat(dt);
    this.cleanupDead();

    if (this.lives <= 0) {
      this.lives = 0;
      this.phase = 'lost';
      return;
    }
    this.checkWaveCleared();
  }

  drainEvents(): SimEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  // ---- WaveSystem / SpawnSystem ----------------------------------------- //

  private updateWaves(dt: number): void {
    if (this.phase === 'intermission') {
      this.intermissionRemaining -= dt;
      if (this.intermissionRemaining <= 0) this.startWave(this.waveIndex + 1);
      return;
    }
    if (this.phase !== 'spawning') return;

    const wave = this.currentWave();
    if (!wave) return;
    wave.groups.forEach((g, i) => {
      const c = this.cursors[i];
      if (c.spawned >= g.count) return;
      c.timer += dt;
      // Hold until the group's start delay has elapsed.
      const elapsed = c.timer - g.startDelayS;
      if (elapsed < 0) return;
      // Spawn as many as the cadence allows this tick.
      while (
        c.spawned < g.count &&
        (c.spawned === 0
          ? elapsed >= 0
          : elapsed >= c.spawned * Math.max(g.spacingS, 0))
      ) {
        this.spawnEnemy(g.enemyId, g.lane);
        c.spawned++;
      }
    });
  }

  private startWave(index: number): void {
    const wave = this.data.level.waves[index];
    if (!wave) {
      // No more waves — victory is detected once stragglers clear.
      this.phase = 'won';
      this.computeStars();
      return;
    }
    this.waveIndex = index;
    this.cursors = wave.groups.map(() => ({ spawned: 0, timer: 0 }));
    this.phase = 'spawning';
  }

  private checkWaveCleared(): void {
    if (this.phase !== 'spawning') return;
    const wave = this.currentWave();
    if (!wave) return;
    const fullySpawned = wave.groups.every(
      (g, i) => this.cursors[i].spawned >= g.count,
    );
    if (!fullySpawned || this.enemies.length > 0) return;

    this.gold += Math.round(wave.rewardGold * this.difficulty.rewardMult);
    const isLast = this.waveIndex >= this.data.level.waves.length - 1;
    if (isLast) {
      this.phase = 'won';
      this.computeStars();
    } else {
      this.phase = 'intermission';
      this.intermissionRemaining = INTERMISSION_S;
      this.restoreSquadsToFull(); // each wave starts with the line whole again
    }
  }

  /** Heal every melee squad to full and revive its fallen — called between waves
   *  so a wave's wear-and-tear doesn't carry over (regen is otherwise paused
   *  while a soldier is holding a foe). */
  private restoreSquadsToFull(): void {
    for (const u of this.units.values()) {
      if (!u.isMelee) continue;
      for (const s of u.soldiers) {
        s.dead = false;
        s.respawnRemaining = 0;
        s.hp = s.maxHp;
      }
      this.refreshSquadAggregate(u);
    }
  }

  private computeStars(): void {
    const obj = this.data.level.starObjectives;
    const livesLost = this.startingLives - this.lives;
    let stars = 1;
    if (obj?.two && this.lives >= this.startingLives * obj.two.minLivesPct) {
      stars = 2;
    }
    if (obj?.three && livesLost <= obj.three.maxLivesLost) {
      stars = 3;
    }
    this.stars = stars;
  }

  private spawnEnemy(enemyId: string, lane = 0, atDistance = 0): void {
    const def = this.data.enemies.get(enemyId);
    if (!def) return;
    const stats = this.resolveEnemyStats(def);
    const e = this.enemyPool.pop() ?? new Enemy();
    // Clamp to a real lane so a mis-authored `lane` can't crash movement.
    const safeLane = lane < this.paths.length ? lane : 0;
    e.spawn(def.id, stats, def.leakCost, def.bounty, def.xp, def.flags.flying, def.isBoss, safeLane);
    // Reset/apply per-spawn flags not covered by Enemy.spawn (pooled reuse).
    e.slowImmune = def.resistKeywords.includes('slowImmune');
    // Adds (shatter) enter partway down the lane where their parent fell.
    if (atDistance > 0) e.distance = Math.min(atDistance, this.paths[safeLane].length);
    this.enemies.push(e);
  }

  private resolveEnemyStats(def: EnemyDef): Stats {
    const base = mergeStats(DEFAULT_STATS, def.stats);
    return {
      ...base,
      hp: base.hp * this.difficulty.enemyHp,
      damage: base.damage * this.difficulty.enemyDmg,
    };
  }

  // ---- StatusSystem ----------------------------------------------------- //

  private updateStatuses(dt: number): void {
    for (const e of this.enemies) {
      if (e.slowRemaining > 0) {
        e.slowRemaining -= dt;
        if (e.slowRemaining <= 0) {
          e.slowFactor = 1;
          e.slowRemaining = 0;
        }
      }
      if (e.burnRemaining > 0) {
        e.burnRemaining -= dt;
        e.hp -= e.burnDps * dt;
        if (e.burnRemaining <= 0) {
          e.burnDps = 0;
          e.burnRemaining = 0;
        }
        if (e.hp <= 0) this.killEnemy(e);
      }
      if (e.sunderRemaining > 0) {
        e.sunderRemaining -= dt;
        if (e.sunderRemaining <= 0) {
          e.armorReduction = 0;
          e.sunderRemaining = 0;
        }
      }
    }
  }

  // ---- UnitSystem (melee regen + respawn) ------------------------------- //

  private updateUnits(dt: number): void {
    for (const u of this.units.values()) {
      if (!u.isMelee || u.soldiers.length === 0) continue;
      for (const s of u.soldiers) {
        if (s.maxHp <= 0) continue;
        if (s.dead) {
          s.respawnRemaining -= dt;
          if (s.respawnRemaining <= 0) {
            s.dead = false;
            s.respawnRemaining = 0;
            s.hp = s.maxHp;
            this.events.push({ kind: 'unitRespawn', x: u.x, y: u.y });
          }
          continue;
        }
        // Regen ONLY while out of combat — a soldier actively holding a foe
        // (engagedDistance set) takes its wounds and cannot heal through them.
        if (s.engagedDistance == null && s.hp < s.maxHp) {
          s.hp = Math.min(s.maxHp, s.hp + s.maxHp * MELEE_REGEN_FRACTION_PER_S * dt);
        }
      }
      this.refreshSquadAggregate(u);
    }
  }

  // ---- PathSystem (movement + blocking + leak) -------------------------- //

  private updatePathAndBlocking(dt: number): void {
    // Alive melee blockers, each with a finite hold capacity (how many enemies
    // it physically stops at once) SHARED across every lane it covers — one
    // soldier stops one enemy, whichever lane that enemy is in. We bucket the
    // blockers' hold lines by lane so an enemy only meets blockers standing on
    // ITS lane (a slot beside the left cascade can't hold the right stream).
    const blockers = [...this.units.values()]
      .filter((u) => u.isMelee)
      .map((u) => ({ slot: u.slotId, cap: this.squadCapacity(u), blocks: u.blocks }))
      .filter((b) => b.cap > 0);
    // lane index -> hold lines on that lane, nearest-spawn first.
    const byLane = new Map<number, { slot: string; d: number }[]>();
    const capBySlot = new Map<string, number>();
    for (const b of blockers) {
      capBySlot.set(b.slot, b.cap);
      for (const blk of b.blocks) {
        const arr = byLane.get(blk.lane) ?? [];
        arr.push({ slot: b.slot, d: blk.distance });
        byLane.set(blk.lane, arr);
      }
    }
    for (const arr of byLane.values()) arr.sort((a, b) => a.d - b.d);

    // Front-to-back (nearest the gate first) so enemies already at a blocker
    // claim its capacity before ones still approaching from behind.
    const ordered = [...this.enemies].sort((a, b) => b.distance - a.distance);
    for (const e of ordered) {
      if (e.dead) continue;
      const path = this.pathFor(e);
      const move = e.stats.moveSpeed * e.slowFactor * dt;

      // Next blocker ahead on this enemy's lane with free capacity. Flyers ignore
      // ground blockers.
      let limit = path.length;
      let limitSlot: string | null = null;
      if (!e.flying) {
        for (const b of byLane.get(e.lane) ?? []) {
          if ((capBySlot.get(b.slot) ?? 0) <= 0) continue;
          if (b.d >= e.distance - 0.5) {
            limit = b.d;
            limitSlot = b.slot;
            break;
          }
        }
      }

      const next = Math.min(e.distance + move, limit);
      if (limitSlot != null && next >= limit - 0.5) {
        e.distance = limit;
        e.engagedUnitSlot = limitSlot;
        capBySlot.set(limitSlot, (capBySlot.get(limitSlot) ?? 0) - 1);
      } else {
        e.distance = next;
        e.engagedUnitSlot = null;
        if (e.distance >= path.length) {
          const p = e.position(path);
          this.lives = Math.max(0, this.lives - e.leakCost);
          this.leaked++;
          this.events.push({ kind: 'leak', x: p.x, y: p.y, cost: e.leakCost, defId: e.defId });
          e.active = false;
          e.dead = true;
          // A boss reaching the gate is an instant defeat, regardless of
          // remaining lives — losing the line to a boss ends the run.
          if (e.isBoss) {
            this.lives = 0;
            this.bossBreached = true;
          }
        }
      }
    }
  }

  // ---- TargetingSystem + CombatSystem ----------------------------------- //

  private updateCombat(dt: number): void {
    // Ranged units fire at enemies in range.
    for (const u of this.units.values()) {
      if (u.isMelee) continue;
      u.attackCooldown = Math.max(0, u.attackCooldown - dt);
      if (u.attackCooldown > 0 || u.stats.attackSpeed <= 0) continue;
      // Multi-shot: fire at up to N independent targets at once (e.g. two archers).
      const targets = this.acquireTargets(u, Math.max(1, u.stats.multiShot));
      if (targets.length === 0) continue;
      for (const t of targets) this.unitHit(u, t);
      u.attackCooldown = 1 / u.stats.attackSpeed;
    }

    // Melee: the squad's living soldiers split the held enemies between them —
    // each soldier holds up to `blockCount`, strikes one, and takes their blows.
    // A soldier falls and respawns INDIVIDUALLY, opening a gap without dropping
    // the whole unit off the line.
    for (const u of this.units.values()) {
      if (!u.isMelee) continue;
      for (const s of u.soldiers) s.engagedDistance = null; // default: wait at the hold line
      const living = u.soldiers.filter((s) => !s.dead);
      if (living.length === 0) continue;
      const engaged = this.enemies
        .filter((e) => !e.dead && e.engagedUnitSlot === u.slotId)
        .sort((a, b) => b.distance - a.distance);
      if (engaged.length === 0) continue;

      // Deal the held enemies out across the soldiers, `blockCount` apiece (the
      // front-most enemies to the front-most soldiers).
      let cursor = 0;
      for (const s of living) {
        const mine = engaged.slice(cursor, cursor + u.blockCount);
        cursor += u.blockCount;
        if (mine.length === 0) break;
        s.engagedDistance = mine[0].distance; // walk out to the foe it holds

        s.attackCooldown = Math.max(0, s.attackCooldown - dt);
        if (s.attackCooldown <= 0 && u.stats.attackSpeed > 0) {
          this.unitHit(u, mine[0]);
          s.attackCooldown = 1 / u.stats.attackSpeed;
        }

        for (const e of mine) {
          e.attackCooldown = Math.max(0, e.attackCooldown - dt);
          if (e.attackCooldown <= 0 && e.stats.attackSpeed > 0) {
            const dmg = resolveDamage({
              damage: e.stats.damage,
              damageType: e.stats.damageType,
              armorPierce: e.stats.armorPierce,
              critMult: e.stats.critMult,
              isCrit: false,
              targetArmor: u.stats.armor,
            });
            s.hp -= dmg;
            e.attackCooldown = 1 / e.stats.attackSpeed;
            if (s.hp <= 0) {
              this.events.push({ kind: 'unitDeath', x: u.x, y: u.y });
              s.hp = 0;
              s.dead = true;
              s.respawnRemaining = MELEE_RESPAWN_S;
              break;
            }
          }
        }
      }
      this.refreshSquadAggregate(u); // soldiers took damage / fell — sync the HUD aggregate
    }
  }

  /** Up to `n` distinct in-range targets, best-first per the unit's targeting mode. */
  private acquireTargets(u: Unit, n: number): Enemy[] {
    const cand: { e: Enemy; key: number }[] = [];
    for (const e of this.enemies) {
      if (e.dead) continue;
      if (e.flying && !u.stats.canHitFlyers) continue;
      const p = e.position(this.pathFor(e));
      const dx = p.x - u.x;
      const dy = p.y - u.y;
      const d2 = dx * dx + dy * dy;
      if (d2 > u.range * u.range) continue;
      let key: number; // higher wins
      switch (u.targetMode) {
        case 'last':
          key = -e.distance;
          break;
        case 'strongest':
          key = e.hp;
          break;
        case 'nearest':
          key = -d2;
          break;
        case 'flyer':
          key = (e.flying ? 1e9 : 0) + e.distance;
          break;
        case 'first':
        default:
          key = e.distance; // closest to the gate
          break;
      }
      cand.push({ e, key });
    }
    if (cand.length <= 1 || n <= 1) {
      return cand.length ? [cand.reduce((a, b) => (b.key > a.key ? b : a)).e] : [];
    }
    cand.sort((a, b) => b.key - a.key);
    return cand.slice(0, n).map((c) => c.e);
  }

  private unitHit(u: Unit, target: Enemy): void {
    const isCrit = this.rng.chance(u.stats.critChance);
    const p = target.position(this.pathFor(target));
    if (!u.isMelee) {
      this.events.push({ kind: 'shot', fromX: u.x, fromY: u.y, toX: p.x, toY: p.y, family: u.family, toFlying: target.flying });
    }
    this.dealHit(u, target, isCrit, 1);

    // Chain lightning: jump to the nearest not-yet-hit enemy, `chainCount` times,
    // with damage falloff each jump (hits MORE units, not a wider area).
    const chains = u.stats.chainCount;
    if (chains > 0) {
      const hit = new Set<Enemy>([target]);
      let from = p;
      let falloff = CHAIN_FALLOFF;
      for (let i = 0; i < chains; i++) {
        let next: Enemy | null = null;
        let bestD2 = CHAIN_RANGE * CHAIN_RANGE;
        for (const e of this.enemies) {
          if (e.dead || hit.has(e)) continue;
          if (e.flying && !u.stats.canHitFlyers) continue;
          const q = e.position(this.pathFor(e));
          const d2 = (q.x - from.x) ** 2 + (q.y - from.y) ** 2;
          if (d2 < bestD2) {
            bestD2 = d2;
            next = e;
          }
        }
        if (!next) break;
        hit.add(next);
        this.dealHit(u, next, isCrit, falloff);
        from = next.position(this.pathFor(next));
        falloff *= CHAIN_FALLOFF;
      }
    }
  }

  /** Apply one unit's hit to a single enemy (mitigation, sunder, Expose, on-hit). */
  private dealHit(u: Unit, e: Enemy, isCrit: boolean, damageScale: number): void {
    const armor = Math.max(0, e.stats.armor - e.armorReduction); // sunder reduces armor
    let dmg = resolveDamage({
      damage: u.stats.damage * damageScale,
      damageType: u.stats.damageType,
      armorPierce: u.stats.armorPierce,
      critMult: u.stats.critMult,
      isCrit,
      targetArmor: armor,
    });
    // "Exposed": an enemy held by a blocker with the `expose` ability takes bonus
    // damage from all attackers — melee HOLDS so the army can burst it (pillar #1).
    if (e.engagedUnitSlot != null && this.blockerExposes(e.engagedUnitSlot)) {
      dmg *= EXPOSED_DAMAGE_MULT;
    }
    e.hp -= dmg;
    this.applyOnHit(u, e);
    const q = e.position(this.pathFor(e));
    this.events.push({ kind: 'damage', x: q.x, y: q.y, amount: dmg, crit: isCrit });
    if (e.hp <= 0) this.killEnemy(e);
  }

  /** Whether the blocker in `slotId` has the `expose` ability at its current tier. */
  private blockerExposes(slotId: string): boolean {
    const u = this.units.get(slotId);
    const def = u && this.data.units.get(u.defId);
    if (!u || !def) return false;
    return tierDefAt(def, u.tier, u.branchId)?.perks.includes('expose') ?? false;
  }

  /** Apply a unit's current-tier on-hit statuses (slow/burn/sunder) to an enemy. */
  private applyOnHit(u: Unit, e: Enemy): void {
    const def = this.data.units.get(u.defId);
    const effects = def && tierDefAt(def, u.tier, u.branchId)?.onHitStatus;
    if (!effects || effects.length === 0) return;
    for (const s of effects) {
      if (s.type === 'slow' || s.type === 'freeze') {
        if (e.slowImmune) continue; // the bronze giant can't be chilled
        // magnitude 0.4 = -40% speed; freeze ~= full slow. Stronger (lower
        // factor) wins; duration refreshes to the longer of the two.
        const factor = s.type === 'freeze' ? 0 : Math.max(0, 1 - s.magnitude);
        e.slowFactor = Math.min(e.slowFactor, factor);
        e.slowRemaining = Math.max(e.slowRemaining, s.durationS);
      } else if (s.type === 'burn' || s.type === 'poison') {
        e.burnDps = Math.max(e.burnDps, s.magnitude);
        e.burnRemaining = Math.max(e.burnRemaining, s.durationS);
      } else if (s.type === 'sunder') {
        // Armor shred — magnitude 0.3 = -0.3 armor. Stronger shred wins.
        e.armorReduction = Math.max(e.armorReduction, s.magnitude);
        e.sunderRemaining = Math.max(e.sunderRemaining, s.durationS);
      }
    }
  }

  private killEnemy(e: Enemy): void {
    if (e.dead) return;
    e.dead = true;
    e.active = false;
    this.gold += e.bounty;
    const p = e.position(this.pathFor(e));
    this.events.push({ kind: 'death', x: p.x, y: p.y, bounty: e.bounty, defId: e.defId });
    // Shatter: a slain enemy with an onDeath spec bursts into adds on its lane,
    // trailing just behind where it fell (they march on toward the gate). Only
    // fires on a KILL — an enemy that LEAKS is removed without calling this.
    const onDeath = this.data.enemies.get(e.defId)?.onDeath;
    if (onDeath) {
      for (let i = 0; i < onDeath.count; i++) {
        const d = Math.max(0, e.distance - (i + 1) * onDeath.spreadDist);
        this.spawnEnemy(onDeath.enemyId, e.lane, d);
      }
    }
  }

  private cleanupDead(): void {
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const e = this.enemies[i];
      if (e.dead || !e.active) {
        this.enemies.splice(i, 1);
        this.enemyPool.push(e);
      }
    }
  }

  // ====================================================================== //
  //  Player actions (called by HUD / BattleScene input)                    //
  // ====================================================================== //

  /**
   * Begin the first wave (from prep) or start the next wave early during an
   * intermission. Calling early simply skips the remaining wait — it grants NO
   * gold bonus (the "call early for gold" reward created bad-feeling FOMO and
   * was removed). `callEarlyBonusPerS` in level data is now ignored.
   */
  startOrCallNextWave(): ActionResult {
    if (this.phase === 'prep') {
      this.startWave(0);
      return { ok: true };
    }
    if (this.phase === 'intermission') {
      this.startWave(this.waveIndex + 1);
      return { ok: true };
    }
    return { ok: false, reason: 'No wave to call right now' };
  }

  /** Whether a unit is in this stage's roster. An omitted `availableUnits`
   *  allowlist means the full roster is available (back-compat default).
   *
   *  The per-stage roster is a CAMPAIGN constraint (it teaches the stage with a
   *  curated kit). Post-game difficulties (tier 4+ / `postGame`) lift it, so the
   *  full roster — mages, frost towers, … — is deployable on any stage. */
  isUnitAvailable(unitId: string): boolean {
    if (this.difficulty.postGame || this.tier >= 4) return true;
    const roster = this.data.level.availableUnits;
    return !roster || roster.includes(unitId);
  }

  deploy(slotId: string, unitId: string): ActionResult {
    const slot = this.slot(slotId);
    if (!slot) return { ok: false, reason: 'No such slot' };
    if (this.units.has(slotId)) return { ok: false, reason: 'Slot occupied' };
    if (!this.isSlotUnlocked(slot)) return { ok: false, reason: 'Slot locked' };
    const def = this.data.units.get(unitId);
    if (!def) return { ok: false, reason: 'No such unit' };
    if (!this.isUnitAvailable(unitId)) return { ok: false, reason: 'Unit not available on this stage' };
    // Slots are universal: any unit may go on any slot. The player decides what
    // to place where and in what volume (GDD pillar #1: positioning is the puzzle).
    const limit = def.deployLimit ?? (def.isHero ? 1 : Infinity);
    if (limit !== Infinity) {
      const placed = [...this.units.values()].filter((u) => u.defId === def.id).length;
      if (placed >= limit) {
        return { ok: false, reason: def.isHero ? 'Hero already deployed' : 'Deploy limit reached' };
      }
    }
    const cost = def.tiers[0].goldCost;
    if (this.gold < cost) return { ok: false, reason: 'Not enough gold' };

    this.gold -= cost;
    const u = new Unit();
    u.slotId = slotId;
    u.defId = def.id;
    u.family = def.family;
    u.x = slot.x;
    u.y = slot.y;
    u.tier = 0;
    u.branchId = null;
    u.targetMode = def.defaultTargetMode;
    u.goldSpent = cost;
    u.isHero = def.isHero;
    u.isMelee = def.family === 'melee';
    if (u.isMelee) {
      u.blockSpan = def.blockSpan ?? 0;
    }
    this.recomputeStats(u, def); // sets blockDistance from the resolved range
    this.units.set(slotId, u);
    return { ok: true };
  }

  /** Recompute a unit's effective stats/range/blockCount/maxTier from its
   *  current tier + chosen branch (the single place that resolves the chain). */
  private recomputeStats(u: Unit, def: UnitDef): void {
    const tiers = effectiveTiers(def, u.branchId);
    u.stats = resolveTierStats(
      tiers.map((t) => t.stats),
      u.tier,
    );
    u.range = u.stats.range;
    u.maxTier = maxTierFor(def, u.branchId);
    if (u.isMelee) {
      u.blockCount = resolveBlockCount(tiers, u.tier, def.blockCount);
      u.squadSize = resolveSquadSize(tiers, u.tier, def.squadSize);
      this.syncSquad(u, u.stats.hp); // resize the line; new bodies arrive at full HP
      this.refreshSquadAggregate(u);
      this.computeBlocks(u); // which lane(s) it holds + where (depends on range)
    }
  }

  /**
   * Resolve which walk lanes a melee squad physically holds and at what path
   * distance on each. A lane is held when the slot sits within `BLOCK_LANE_REACH`
   * of it — so on a fork a slot beside one cascade holds only that stream, while
   * a slot on the shared run (lanes coincident) holds both. The nearest lane is
   * the PRIMARY (used for rendering and `blockDistance`). Falls back to lane 0 so
   * a blocker is never left holding nothing on an oddly-placed slot.
   */
  private computeBlocks(u: Unit): void {
    const reach = Math.max(BLOCK_LANE_REACH, u.range);
    const blocks: { lane: number; distance: number; gap: number }[] = [];
    this.paths.forEach((path, lane) => {
      const d = path.nearestDistance({ x: u.x, y: u.y });
      const p = path.getPointAt(d);
      const gap = Math.hypot(p.x - u.x, p.y - u.y);
      if (gap <= reach) blocks.push({ lane, distance: this.meleeEngageDistance(u, path), gap });
    });
    if (blocks.length === 0) {
      // No lane within reach — hold the single nearest lane so the unit still works.
      let best = 0;
      let bestGap = Infinity;
      this.paths.forEach((path, lane) => {
        const p = path.getPointAt(path.nearestDistance({ x: u.x, y: u.y }));
        const gap = Math.hypot(p.x - u.x, p.y - u.y);
        if (gap < bestGap) { bestGap = gap; best = lane; }
      });
      blocks.push({ lane: best, distance: this.meleeEngageDistance(u, this.paths[best]), gap: bestGap });
    }
    blocks.sort((a, b) => a.gap - b.gap);
    u.blocks = blocks.map((b) => ({ lane: b.lane, distance: b.distance }));
    u.blockLane = blocks[0].lane;
    u.blockDistance = blocks[0].distance;
  }

  /**
   * Where a melee squad meets the enemy: the path distance at the LEADING edge
   * of the building's range (the point a soldier walks out to and holds). A
   * larger range intercepts foes further up the lane. Range 0 (e.g. test walls)
   * falls back to the path point nearest the building.
   */
  private meleeEngageDistance(u: Unit, path: Path): number {
    const d0 = path.nearestDistance({ x: u.x, y: u.y });
    if (u.range <= 0) return d0;
    const step = 4;
    let d = d0;
    while (d - step > 0) {
      const p = path.getPointAt(d - step);
      if (Math.hypot(p.x - u.x, p.y - u.y) > u.range) break;
      d -= step;
    }
    return d;
  }

  /**
   * Reconcile a melee unit's soldier roster to `squadSize` at `perSoldierMaxHp`.
   * Upgrading to a larger squad marches fresh full-HP soldiers in; each existing
   * soldier keeps its wound ratio across the tier (so an upgrade mid-fight isn't
   * a free heal). A fallen soldier stays fallen — its respawn keeps counting.
   */
  private syncSquad(u: Unit, perSoldierMaxHp: number): void {
    while (u.soldiers.length < u.squadSize) {
      const s = new Soldier();
      s.maxHp = perSoldierMaxHp;
      s.hp = perSoldierMaxHp;
      u.soldiers.push(s);
    }
    if (u.soldiers.length > u.squadSize) u.soldiers.length = u.squadSize;
    for (const s of u.soldiers) {
      const ratio = s.maxHp > 0 ? s.hp / s.maxHp : 1;
      s.maxHp = perSoldierMaxHp;
      if (!s.dead) s.hp = perSoldierMaxHp * ratio;
    }
  }

  /** Recompute a squad's aggregate HP/dead/respawn from its soldiers (for HUD). */
  private refreshSquadAggregate(u: Unit): void {
    let hp = 0;
    let maxHp = 0;
    let living = 0;
    let soonest = Infinity;
    for (const s of u.soldiers) {
      maxHp += s.maxHp;
      if (s.dead) soonest = Math.min(soonest, s.respawnRemaining);
      else {
        hp += s.hp;
        living++;
      }
    }
    u.hp = hp;
    u.maxHp = maxHp;
    u.dead = u.soldiers.length > 0 && living === 0;
    u.respawnRemaining = soonest === Infinity ? 0 : soonest;
  }

  /** A squad's total hold capacity: living soldiers × per-soldier blockCount. */
  private squadCapacity(u: Unit): number {
    let living = 0;
    for (const s of u.soldiers) if (!s.dead) living++;
    return living * u.blockCount;
  }

  /**
   * Upgrade a unit one step. At the branch point a `branchId` MUST be supplied
   * (the player's specialization choice); afterwards upgrades follow that path.
   */
  upgrade(slotId: string, branchId?: string): ActionResult {
    const u = this.units.get(slotId);
    if (!u) return { ok: false, reason: 'Empty slot' };
    const def = this.data.units.get(u.defId);
    if (!def) return { ok: false, reason: 'Missing unit def' };

    if (isBranchPoint(def, u.tier, u.branchId)) {
      if (!branchId) return { ok: false, reason: 'Choose an upgrade path' };
      const branch = def.branches.find((b) => b.id === branchId);
      if (!branch) return { ok: false, reason: 'No such path' };
      const cost = branch.tiers[0].goldCost;
      if (this.gold < cost) return { ok: false, reason: 'Not enough gold' };
      this.gold -= cost;
      u.goldSpent += cost;
      u.branchId = branchId;
      u.tier += 1;
      this.recomputeStats(u, def);
      return { ok: true };
    }

    const tiers = effectiveTiers(def, u.branchId);
    const next = u.tier + 1;
    if (next >= tiers.length) return { ok: false, reason: 'Max tier' };
    const cost = tiers[next].goldCost;
    if (this.gold < cost) return { ok: false, reason: 'Not enough gold' };
    this.gold -= cost;
    u.goldSpent += cost;
    u.tier = next;
    this.recomputeStats(u, def);
    return { ok: true };
  }

  /** What the next upgrade offers: a one-time branch choice, a linear step, or max. */
  upgradeOptions(
    slotId: string,
  ):
    | { kind: 'branch'; options: { id: string; name: string; desc?: string; cost: number }[] }
    | { kind: 'upgrade'; cost: number }
    | { kind: 'max' }
    | null {
    const u = this.units.get(slotId);
    if (!u) return null;
    const def = this.data.units.get(u.defId);
    if (!def) return null;
    if (isBranchPoint(def, u.tier, u.branchId)) {
      return {
        kind: 'branch',
        options: def.branches.map((b) => ({
          id: b.id,
          name: b.name,
          desc: b.desc,
          cost: b.tiers[0].goldCost,
        })),
      };
    }
    const tiers = effectiveTiers(def, u.branchId);
    const next = u.tier + 1;
    if (next < tiers.length) return { kind: 'upgrade', cost: tiers[next].goldCost };
    return { kind: 'max' };
  }

  /** The active tier definition for a deployed unit (for UI: perks, statuses). */
  activeTierDef(slotId: string): import('@/data/schema/unit').UnitTier | undefined {
    const u = this.units.get(slotId);
    const def = u && this.data.units.get(u.defId);
    return u && def ? tierDefAt(def, u.tier, u.branchId) : undefined;
  }

  sell(slotId: string): ActionResult {
    const u = this.units.get(slotId);
    if (!u) return { ok: false, reason: 'Empty slot' };
    const def = this.data.units.get(u.defId);
    const refund = Math.floor(u.goldSpent * (def?.sellRefund ?? 0.5));
    this.gold += refund;
    this.units.delete(slotId);
    return { ok: true };
  }

  setTargeting(slotId: string, mode: TargetMode): ActionResult {
    const u = this.units.get(slotId);
    if (!u) return { ok: false, reason: 'Empty slot' };
    u.targetMode = mode;
    return { ok: true };
  }

  // ====================================================================== //
  //  Queries (read-only, for UI)                                           //
  // ====================================================================== //

  slot(slotId: string): Slot | undefined {
    return this.data.level.slots.find((s) => s.id === slotId);
  }

  isSlotUnlocked(slot: Slot): boolean {
    return slot.lockedUntilWave <= Math.max(0, this.waveIndex);
  }

  currentWave(): Wave | undefined {
    return this.waveIndex >= 0 ? this.data.level.waves[this.waveIndex] : undefined;
  }

  nextWave(): Wave | undefined {
    return this.data.level.waves[this.waveIndex + 1];
  }

  totalWaves(): number {
    return this.data.level.waves.length;
  }

  /**
   * Deployable units for an empty slot — slots are universal, so this is the
   * full core roster (trophies excluded), with affordability and per-unit
   * deploy-limit state for the picker. `_slotType` is accepted for call-site
   * compatibility but no longer restricts placement.
   */
  deployableFor(
    _slotType?: string,
  ): { def: UnitDef; affordable: boolean; atLimit: boolean }[] {
    const out: { def: UnitDef; affordable: boolean; atLimit: boolean }[] = [];
    for (const def of this.data.units.values()) {
      if (def.isTrophy || !this.isUnitAvailable(def.id)) continue;
      const limit = def.deployLimit ?? (def.isHero ? 1 : Infinity);
      const placed =
        limit === Infinity
          ? 0
          : [...this.units.values()].filter((u) => u.defId === def.id).length;
      out.push({
        def,
        affordable: this.gold >= def.tiers[0].goldCost,
        atLimit: placed >= limit,
      });
    }
    return out;
  }
}
